import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'

import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import Heading from 'LuxKit/Typography/Heading'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import { hasBundleInclusions as getHasBundleInclusions } from 'lib/cruises/cruiseBundleFlightCredits'
import ModalContext from 'contexts/ModalContext'
import CruiseDealTermsConditionsModal from 'components/Cruises/CruiseDealPage/CruiseDealTermsConditionsModal'
import CruiseBundleInclusions from './CruiseBundleInclusions'
import CruiseLuxPlusOnboardCreditLabel from './CruiseLuxPlusOnboardCreditLabel'
import CruiseLimitedInclusionList from './CruiseLimitedInclusionList'
import { MAX_VISIBLE_STANDARD_INCLUSIONS } from './cruiseStandardInclusionUtils'

const InclusionSection = styled.div`
  padding: 0 ${rem(4)};
`

interface Props {
  offer: App.CruiseOffer
  evergreenInclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  ports?: Array<string>
  gap?: React.ComponentProps<typeof Group>['gap']
  hideHeader?: boolean
}

function CruiseInclusionList({
  offer,
  standardInclusions,
  luxPlusInclusions,
  evergreenInclusions,
  tileInclusions,
  ports,
  gap = 8,
  hideHeader = false,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const allStandardInclusions = useMemo(
    () => buildAllStandardInclusions(standardInclusions, tileInclusions),
    [standardInclusions, tileInclusions],
  )

  const hasStandardInclusions = allStandardInclusions.some(
    (inclusion) => inclusion.displayType !== 'BOOKING_CONDITION',
  )
  const hasBundleInclusions = getHasBundleInclusions(offer.bundle)
  const primaryDealPageTerms = offer.dealPages?.[0]?.termsAndConditions
  const hasDealPageTerms = !!primaryDealPageTerms
  const hasInclusions = hasStandardInclusions || !!luxPlusInclusions?.length || !!evergreenInclusions?.length || hasBundleInclusions
  const showDealPageTermsModal = useCallback(() => {
    if (!primaryDealPageTerms) return

    showModal(<CruiseDealTermsConditionsModal termsAndConditions={primaryDealPageTerms} />)
  }, [primaryDealPageTerms, showModal])

  if (!hasInclusions) return null

  return <Group direction="vertical" gap={gap}>
    {!hideHeader && <Group direction="horizontal" gap={8} verticalAlign="center">
      <Heading variant="heading6">
        {intl.formatMessage({
          id: '9mA3D3AK0szzNlDAGrmh',
          defaultMessage: 'What\u2019s included',
        })}
      </Heading>
      <NeutralLabel>
        {intl.formatMessage({
          id: 'Vr7pOXF7YqVxL0JiVVuM',
          defaultMessage: 'select packages only',
        })}
      </NeutralLabel>
    </Group>}

    <Group direction="vertical" gap={20}>
      <BodyTextBlock
        variant="large"
        weight="bold"
        startIcon={<LineShipIcon colour="highlight-primary-dark" size="S" />}
      >
        {intl.formatMessage({
          id: 'kOwmAbePYrVdCY1Tl2t_',
          defaultMessage: 'Cruise',
        })}
      </BodyTextBlock>

      <InclusionSection>
        <Group direction="vertical" gap={8} horizontalAlign="start">
          <CruiseLuxPlusOnboardCreditLabel
            luxPlusInclusions={luxPlusInclusions}
            offer={offer}
            isModalPrimaryActionHidden
          />

          <Group direction="vertical" gap={4}>
            {hasStandardInclusions && <CruiseLimitedInclusionList
              inclusions={allStandardInclusions}
              visibleItems={MAX_VISIBLE_STANDARD_INCLUSIONS}
              showMoreOnSeparateLine
            />}
          </Group>

          <LoyaltyInclusions offer={offer} variant="offer-page" />

          {!!evergreenInclusions?.length && <CruiseLimitedInclusionList
            inclusions={evergreenInclusions}
            visibleItems={5}
          />}

          {hasDealPageTerms && <TextLink
            size="medium"
            colour="normal-contrast"
            onClick={showDealPageTermsModal}
          >
            {intl.formatMessage({
              id: '9QK_nLe33AUacf5_TuRz',
              defaultMessage: 'T&Cs apply',
            })}
          </TextLink>}

          {!!ports?.length && <BodyText variant="medium" weight="normal">
            <FormattedMessage
              id="0fzM8feaDW9phy6hxsqT"
              defaultMessage="Your journey takes you to: <bold>{ports}</bold>"
              
              values={{
                ports: ports.join(' - '),
                bold: (chunks: React.ReactNode) => (
                  <BodyText variant="medium" weight="bold" as="span">
                    {chunks}
                  </BodyText>
                ),
              }}
            />
          </BodyText>}
        </Group>
      </InclusionSection>

      {hasBundleInclusions && offer.bundle && <CruiseBundleInclusions
        bundle={offer.bundle}
      />}
    </Group>
  </Group>
}

export default CruiseInclusionList
